import { ChangeEvent, FC } from "react"
import classNames from "classnames"
import { useSelector } from "react-redux"
import Actions from "./Actions"
import Address from "./Address"
import NameFields from "./NameFields"
import Cell from "../Cell"
import TextField from "../TextField"
import Spinner, { SpinnerSize } from "@/components/shared/Spinner"
import useFormStateHandler from "@/hooks/useFormStateHandler"
import { fieldConfig } from "@/pages/ControlPanel/config"
import { RootState } from "@/store"
import { CustomerForm } from "@/types/customer"
import { Facility } from "@/types/facility"
import { getErrorMessage } from "@/utils/index"

interface NewCustomerProps {
   onSave: (customerForm: CustomerForm) => void
   cancel: () => void
}

export const initialFormData: CustomerForm = {
   name: "",
   facility: {
      name: "",
      address: {
         country: "",
         state: "",
         city: "",
         zipCode: "",
         street: "",
         timezone: null,
      },
      entraTid: "",
   },
}

const NewCustomer: FC<NewCustomerProps> = ({ onSave, cancel }) => {
   const { loadingCustomer, errorCustomer } = useSelector(
      ({ customersReducer }: RootState) => customersReducer
   )
   const [customerFormState, setState] =
      useFormStateHandler<CustomerForm>(initialFormData)

   const handleFieldChange =
      (field: keyof CustomerForm) => (e: ChangeEvent<HTMLInputElement>) => {
         setState({ ...customerFormState, [field]: e.target.value })
      }

   const handleFacilityFieldChange =
      (field: keyof CustomerForm["facility"]) =>
      (e: ChangeEvent<HTMLInputElement>) => {
         setState({
            ...customerFormState,
            facility: {
               ...customerFormState.facility,
               [field]: e.target.value,
            } as CustomerForm["facility"],
         })
      }

   const handleCustomerNameChange = handleFieldChange("name")
   const handleFacilityNameChange = handleFacilityFieldChange(
      "name" as keyof CustomerForm["facility"]
   )
   const handleEntraTidChange = handleFacilityFieldChange(
      "entraTid" as keyof CustomerForm["facility"]
   )

   const isCustomerNameValid = fieldConfig.customerName?.validate(
      customerFormState.name
   )
   const isFacilityNameValid = fieldConfig.name?.validate(
      customerFormState.facility?.name as Facility["name"]
   )
   const isCountryValid = fieldConfig.country?.validate(
      customerFormState.facility?.address.country || "USA"
   )
   const isEntraTidValid = fieldConfig.entraTid?.validate(
      customerFormState.facility?.entraTid || ""
   )

   const isFormValid =
      isCustomerNameValid &&
      isFacilityNameValid &&
      isCountryValid &&
      isEntraTidValid

   return (
      <div
         className={classNames(
            "customers-facilities-table__group--customer",
            "customers-facilities-table__group--single-facility",
            {
               "customers-facilities-table__group--loading": loadingCustomer,
               "customers-facilities-table__group--error": errorCustomer,
            }
         )}
      >
         {loadingCustomer ? (
            <Spinner size={SpinnerSize.SMALL} />
         ) : errorCustomer ? (
            getErrorMessage(
               errorCustomer,
               "Incorrect information, please refresh the page and try again"
            )
         ) : (
            <>
               <span className="customers-facilities-table__customer-info">
                  <NameFields
                     customerName={customerFormState.name}
                     facilityName={
                        customerFormState.facility?.name as Facility["name"]
                     }
                     onCustomerNameChange={handleCustomerNameChange}
                     onFacilityNameChange={handleFacilityNameChange}
                     isCustomerNameValid={isCustomerNameValid}
                     isFacilityNameValid={isFacilityNameValid}
                  />
                  <Address
                     address={
                        customerFormState.facility
                           ?.address as Facility["address"]
                     }
                     setAddress={(address) =>
                        setState({
                           ...customerFormState,
                           facility: {
                              ...customerFormState.facility,
                              address,
                           } as CustomerForm["facility"],
                        })
                     }
                  />
                  <span className="customers-facilities-table__entra-tid">
                     <Cell
                        name="entraTid"
                        value={customerFormState.facility?.entraTid || ""}
                        isEditMode
                        error={!isEntraTidValid}
                        handleCellChange={handleEntraTidChange}
                        placeholder={fieldConfig.entraTid?.placeholder}
                     >
                        <TextField name="entraTid">
                           {customerFormState.facility?.entraTid || "-"}
                        </TextField>
                     </Cell>
                  </span>
               </span>
               <Actions
                  isFormValid={isFormValid || false}
                  onSave={() => {
                     const formWithUSA = {
                        ...customerFormState,
                        facility: {
                           ...customerFormState.facility,
                           address: {
                              ...customerFormState.facility?.address,
                              country: "USA",
                           },
                        },
                     }
                     onSave(formWithUSA as CustomerForm)
                  }}
                  onCancel={cancel}
               />
            </>
         )}
      </div>
   )
}

export default NewCustomer
